import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Select, Spin } from 'antd';
import PropTypes from 'prop-types';
import { useDictionaryTranslation } from '../../../../hooks/useDictionaryTranslation';
import { useNotifications } from '../../../../hooks/useNotifications';
import { FormWrapper } from '../../../../components/formWrapper/form-wrapper';
import { AccountsService } from '../../../../services/accountsService';
import { CustomListsService } from '../../../../services/customListService';

export const DuplicateListModal = ({ customListId, show, handleClose }) => {
  const [form] = Form.useForm();
  const { translate: t } = useDictionaryTranslation();
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const [accounts, setAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = () => {
    form.resetFields();
    handleClose();
  };

  const loadAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      const { accounts: data } = await AccountsService.getSelectableAccounts();
      setAccounts(data ?? []);
    } catch (error) {
      showErrorNotification({
        title: 'error',
        description: 'errorRetrievingRecords',
      });
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const handleSubmit = async () => {
    const { targetAccountId } = form.getFieldsValue();
    setIsSubmitting(true);

    try {
      await CustomListsService.duplicateListToAccount({ listId: customListId, targetAccountId });
      showSuccessNotification({
        title: 'success',
        description: 'successDuplicatingRecord',
      });
      closeModal();
    } catch (error) {
      showErrorNotification({
        title: 'error',
        description: 'errorDuplicatingRecord',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    form.resetFields();
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <Modal
      title={t('duplicateInAnotherAccount')}
      centered
      destroyOnClose
      width="30%"
      onCancel={closeModal}
      visible={show}
      footer={
        <div>
          <Button
            form="duplicateList"
            key="submit"
            htmlType="submit"
            type="primary"
            disabled={isSubmitting || isLoadingAccounts}
          >
            {t('duplicate')}
          </Button>
        </div>
      }
    >
      <Spin spinning={isSubmitting}>
        <FormWrapper>
          <Form name="duplicateList" layout="vertical" form={form} onFinish={handleSubmit}>
            <Row>
              <Col xs={24} md={24}>
                <Form.Item
                  name="targetAccountId"
                  label={t('destinationAccount')}
                  rules={[{ required: true, message: t('selectAnAccount') }]}
                >
                  <Select
                    showSearch
                    loading={isLoadingAccounts}
                    placeholder={t('selectAnAccount')}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {accounts.map((account) => (
                      <Select.Option key={account._id} value={account._id}>
                        {account.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </FormWrapper>
      </Spin>
    </Modal>
  );
};

DuplicateListModal.propTypes = {
  show: PropTypes.bool,
  customListId: PropTypes.string,
  handleClose: PropTypes.func,
};

export default DuplicateListModal;
